import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { PdfBrandDialog } from "./PdfBrandDialog";
import { loadPdfBrand, loadClientPdfBrand, openHsePdf } from "../../lib/pdfBrand";
import { FilePdf, CaretDown, Star, SlidersHorizontal, Check, Buildings } from "@phosphor-icons/react";

/**
 * Split button for any branded PDF: the main click downloads with the effective default — the client's preset when `clientId`
 * has one, else the workspace default, else company branding as-is; the caret lists saved presets and opens the branding options.
 * `url` is the API path of the PDF (with or without /api, query string allowed).
 */
export function PdfDownloadButton({ url, label = "PDF", testid = "pdf-download", size = "sm", variant = "outline", className = "", disabled = false, iconOnly = false, title, clientId }) {
  const [brand, setBrand] = useState(null);
  const [client, setClient] = useState(null);
  const [dlg, setDlg] = useState(false);
  useEffect(() => {
    let alive = true;
    const refresh = () => { loadPdfBrand().then((d) => alive && setBrand(d)); loadClientPdfBrand(clientId).then((c) => alive && setClient(c)); };
    refresh();
    window.addEventListener("pdf-brand-changed", refresh);
    return () => { alive = false; window.removeEventListener("pdf-brand-changed", refresh); };
  }, [clientId]);
  const go = (sel) => openHsePdf(url, sel).catch(() => toast.error("Couldn't build the PDF"));
  const presets = brand?.presets || [];
  const clientPick = client?.preset_id || "";
  const effectiveId = clientPick === "none" ? "" : (client?.effective_id || brand?.default_id || "");
  const def = presets.find((p) => p.id === effectiveId);
  const forClient = !!clientPick && clientPick !== "none" && !!def;
  const hint = def ? `Download with preset “${def.name}”${forClient ? " (set for this client)" : ""}` : "Download PDF";
  return (
    <div className={`inline-flex items-stretch ${className}`} data-testid={`${testid}-group`}>
      <Button size={size} variant={variant} disabled={disabled} onClick={() => go()} title={title || hint} className="rounded-r-none border-r-0" data-testid={testid}>
        <FilePdf size={14} className={iconOnly ? "" : "mr-1"} /> {!iconOnly && label}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size={size} variant={variant} disabled={disabled} className="rounded-l-none px-1.5" title="Branding presets & options" data-testid={`${testid}-menu`}><CaretDown size={12} /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64" data-testid={`${testid}-menu-content`}>
          <DropdownMenuLabel className="text-[11px] text-muted-foreground">Download with…</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => go({ presetId: "none" })} data-testid={`${testid}-preset-none`}>
            {!def && <Check size={12} className="mr-1 text-primary" />}<span className={def ? "ml-4" : ""}>Company branding (as-is)</span>
          </DropdownMenuItem>
          {presets.map((p) => (
            <DropdownMenuItem key={p.id} onClick={() => go({ presetId: p.id })} data-testid={`${testid}-preset-${p.id}`}>
              {p.id === effectiveId ? <Check size={12} className="mr-1 text-primary" /> : <span className="w-4" />}{p.name}
              {forClient && p.id === effectiveId ? <Buildings size={11} className="ml-auto text-primary" title="Set on the client record" data-testid={`${testid}-client-badge`} /> : p.is_default && <Star size={10} weight="fill" className="ml-auto text-amber-500" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setDlg(true)} data-testid={`${testid}-options`}><SlidersHorizontal size={13} className="mr-2" /> Branding options…</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <PdfBrandDialog open={dlg} onClose={() => setDlg(false)} url={url} initialPresetId={forClient ? effectiveId : undefined} />
    </div>
  );
}
